import { PersonLane } from '@app/modules/persons/appearances/types';
import { ListItem, ListItemAvatar, ListItemText } from '@mui/material';
import { usePersonLaneIdentity } from '../../hooks/usePersonLaneIdentity';
import { PersonLaneAvatar } from '../../shared/PersonLaneAvatar';
import { APPEARANCE_BAR_DIMMED_OPACITY } from '../../shared/persons-track-visual-tokens';

const OVERFLOW_AVATAR_PX = 40;

type PersonCastOverflowRowProps = {
  lane: PersonLane;
  dimmed?: boolean;
  onHover: () => void;
};

/**
 * One person row inside the cast "+N" overflow list.
 *
 * Shows avatar, display name, and optional occupation. Hidden until identity
 * is ready. Hover tells the track to highlight this person's bars; click does
 * nothing.
 */
export function PersonCastOverflowRow({
  lane,
  dimmed = false,
  onHover,
}: PersonCastOverflowRowProps) {
  const { nameNode, profession, isIdentityReady } = usePersonLaneIdentity(lane);

  if (!isIdentityReady) {
    return null;
  }

  return (
    <ListItem
      divider
      onMouseEnter={onHover}
      sx={{
        px: 1.5,
        py: 1,
        // Null (not-ready) siblings are omitted from the DOM, so this hits the last visible row.
        '&:last-of-type': { borderBottom: 0 },
        opacity: dimmed ? APPEARANCE_BAR_DIMMED_OPACITY : 1,
        transition: 'opacity 120ms',
      }}
    >
      <ListItemAvatar sx={{ minWidth: 0, mr: 1.25 }}>
        <PersonLaneAvatar
          lane={lane}
          sizePx={OVERFLOW_AVATAR_PX}
          borderWidth={3}
          dashedWhenUnknown
        />
      </ListItemAvatar>
      <ListItemText
        primary={nameNode}
        secondary={profession || undefined}
        primaryTypographyProps={{ variant: 'body2', noWrap: true }}
        secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
        sx={{ my: 0, minWidth: 0 }}
      />
    </ListItem>
  );
}
